import { Typography } from '@euler/app/components/typography';
import { PrimaryButton } from '@euler/app/flows/ai-suite/components/PrimaryButton';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { unanswerableGaps } from '@euler/functions/apdm/import/importState';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  ApdmImportQuestion,
  ApdmReadingAnswers,
  ApdmReadingGap,
} from '@euler/model/apdm';
import { useState } from 'react';
import { View } from 'react-native';
import { readingGapLabel } from '../../functions/readingGapLabel';
import { GapAnswer } from './GapAnswer';

/** The one question asked when nothing useful can be imported without it. */
export function ImportQuestion({
  question,
  busy,
  onAnswer,
}: {
  question: ApdmImportQuestion;
  busy: boolean;
  onAnswer: (answers: ApdmReadingAnswers) => void;
}) {
  const { rpx } = useResponsiveContext();
  const [answers, setAnswers] = useState<ApdmReadingAnswers>({});
  const fitting = (gap: ApdmReadingGap) =>
    question.columns.filter(column => column.fits.includes(gap));
  // The reader refuses a column whose values do not look the part, so a
  // gap no column fits cannot be answered here.
  const missing = unanswerableGaps(question);
  if (missing.length)
    return (
      <Typography.Text
        size={rpx(13)}
        lineHeight={rpx(20)}
        color={aiColors.foreground}
      >
        这次导入需要{missing.map(readingGapLabel).join('、')}
        ，但这份表格里没有找到对应的列。请换一份有这些内容的表格。
      </Typography.Text>
    );
  const complete = question.gaps.every(gap => answers[gap] !== undefined);
  // One column answers one gap: picking it for another moves it there.
  const choose = (gap: ApdmReadingGap, columnIndex: number) =>
    setAnswers(current => ({
      ...Object.fromEntries(
        Object.entries(current).filter(([, index]) => index !== columnIndex),
      ),
      [gap]: columnIndex,
    }));
  return (
    <View style={{ gap: rpx(16) }}>
      <Typography.Text size={rpx(13)} color={aiColors.mutedForeground}>
        没能自动认出下面的内容，请选出对应的列。
      </Typography.Text>
      {question.gaps.map(gap => (
        <GapAnswer
          key={gap}
          gap={gap}
          columns={fitting(gap)}
          value={answers[gap]}
          disabled={busy}
          onChange={columnIndex => choose(gap, columnIndex)}
        />
      ))}
      <PrimaryButton
        title="继续导入"
        loading={busy}
        disabled={!complete}
        onPress={() => onAnswer(answers)}
      />
    </View>
  );
}
